import {
  browserProfilingIntegration,
  browserTracingIntegration,
  getCurrentHub,
  init,
  showReportDialog,
} from "@sentry/vue";
import { captureConsoleIntegration } from "@sentry/integrations";
import { LogFunctionKey } from "~/injectionKeys";
import type { Client } from "@sentry/types";
import { log } from "~/logging/log";

export default defineNuxtPlugin((nuxtApp) => {
  const runtimeConfig = useRuntimeConfig();
  const appConfig = useAppConfig();
  const router = useRouter();

  let sentryClient: Client | undefined = undefined;
  if (runtimeConfig.public.sentry.enable) {
    // Important: This is client-side (browser) only.
    // For the server-side setup, see `server/plugins/sentry.ts`.
    // Try to keep these in sync with each other.
    init({
      app: nuxtApp.vueApp,
      dsn: runtimeConfig.public.sentry.dsn,
      environment: runtimeConfig.public.sentry.environment,
      release: appConfig.build.release,
      integrations: [
        // See https://docs.sentry.io/platforms/javascript/profiling/
        browserTracingIntegration({ router }),
        browserProfilingIntegration(),
        captureConsoleIntegration({ levels: ["warn", "error"] }),
      ],
      tracesSampleRate: runtimeConfig.public.sentry.tracesSampleRate,
      tracePropagationTargets: [
        // All targets in this list MUST allow the `Baggage` and `Sentry-Trace` headers in their CORS policy.
        "localhost",
        /^\//,
        runtimeConfig.public.site.url,
        runtimeConfig.public.scripture.baseUrl,
        // Solr has no Sentry instrumentation, so don't include it here.
        // Drupal should never be called directly, so we don't include it either.
      ],
      // strictTraceContinuation: true, // TODO: Enable after upgrading to Sentry v10
      profilesSampleRate: runtimeConfig.public.sentry.profilesSampleRate,
      attachProps: true,
      logErrors: false,
      ignoreErrors: [
        // We don't want users trying the wrong URLs to use up our Sentry quota.
        "Page not found",
      ],
    });

    // Unfortunately, Sentry uses globals, so `init` does not return an instance of a Sentry client.
    // We have to get it from the hub.
    sentryClient = getCurrentHub().getClient();
  }

  nuxtApp.vueApp.provide(LogFunctionKey, (args) => {
    args.extra = {
      ...args.extra,
      server: false,
    };

    log({
      ...args,
      sentryClient,
      promptForUserFeedback: showReportDialog,
    });
  });
});
